import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { captureError } from '@/lib/sentry'
import type { TimePeriod } from '@/types/dashboard'
import { periodToMs } from '@/types/dashboard'
import type { TimeSeriesPoint } from '@/types/timeseries'
import { getBucketConfig, createEmptyBuckets, getBucketIndex } from '@/types/timeseries'

export interface UserTimeSeries {
  userGrowth: TimeSeriesPoint[]      // Cumulative unique users over time
  dailyEngagement: TimeSeriesPoint[] // Comments + likes per bucket
}

export function useUserTimeSeries(period: TimePeriod) {
  return useQuery({
    queryKey: ['user-timeseries', period],
    queryFn: async (): Promise<UserTimeSeries> => {
      const now = Date.now()
      const periodMs = periodToMs(period)
      const periodStartMs = now - periodMs
      const config = getBucketConfig(period)

      const userGrowth = createEmptyBuckets(period)
      const dailyEngagement = createEmptyBuckets(period)

      try {
        // Fetch first-seen timestamp per wallet via RPC. The underlying
        // user_sessions table exceeds PostgREST's 1000-row .select() cap, so
        // we must aggregate server side.
        // See dextoro-admin/supabase/migrations/20260409000002_dashboard_user_stats_rpc.sql.
        const { data: firstSeenRows } = await supabase.rpc('get_wallet_first_seen')

        // Rows arrive pre-sorted by first_seen_at ASC from the RPC.
        const firstSeenEntries: Date[] = (firstSeenRows ?? []).map(
          (r: { first_seen_at: string }) => new Date(r.first_seen_at)
        )

        // Count cumulative users up to start of period
        let cumulativeBefore = 0
        for (const d of firstSeenEntries) {
          if (d.getTime() < periodStartMs) cumulativeBefore++
        }

        // Bucket new users per period and accumulate
        const newUsersPerBucket = new Array(config.bucketCount).fill(0)
        for (const d of firstSeenEntries) {
          const idx = getBucketIndex(d.getTime(), periodStartMs, config.bucketMs, config.bucketCount)
          if (idx >= 0) newUsersPerBucket[idx]++
        }

        let running = cumulativeBefore
        for (let i = 0; i < config.bucketCount; i++) {
          running += newUsersPerBucket[i]
          userGrowth[i].value = running
        }

        // Fetch comments and likes for engagement.
        // TODO(dashboard-truncation): these are unranged .select() calls and
        // will start truncating once either table exceeds 1000 rows. Migrate
        // to a bucketed RPC in a follow-up PR.
        const [commentsResult, likesResult] = await Promise.all([
          supabase.from('token_comments').select('created_at').eq('is_deleted', false),
          supabase.from('token_likes').select('created_at'),
        ])

        commentsResult.data?.forEach((c: { created_at: string }) => {
          const idx = getBucketIndex(new Date(c.created_at).getTime(), periodStartMs, config.bucketMs, config.bucketCount)
          if (idx >= 0) dailyEngagement[idx].value++
        })

        likesResult.data?.forEach((l: { created_at: string }) => {
          const idx = getBucketIndex(new Date(l.created_at).getTime(), periodStartMs, config.bucketMs, config.bucketCount)
          if (idx >= 0) dailyEngagement[idx].value++
        })
      } catch (error) {
        captureError(
          error instanceof Error ? error : new Error('useUserTimeSeries: fetch failed'),
          { feature: 'admin_dashboard', component: 'useUserTimeSeries' }
        )
      }

      return { userGrowth, dailyEngagement }
    },
    staleTime: 60_000,
    refetchInterval: 60_000,
  })
}
